import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '../store/StoreContext';
import { useAuth } from '../auth/AuthContext';
import db from '../lib/database';
import {
    thumbProps,
    getThumbnailUrl,
    processImageForUpload,
    uploadInPool,
    UPLOAD_CONCURRENCY,
} from '../lib/imageUtils';
import WalkthroughClientIntake from '../components/forms/WalkthroughClientIntake';
import RoutePointsEditor from '../components/forms/RoutePointsEditor';
import ClientNotesPanel from '../components/forms/ClientNotesPanel';
import { PACKING_OPTIONS, getFurnitureClassOptions } from '../lib/constants';
// Moves (2026-09-18): dates / addresses / job type live on the client's
// CURRENT MOVE (moves-architecture.md §3 rule 1) — the Details tab edits
// that row, the list + header read through it.
import { currentMoveFor, movesForClient, fmtMoveDate, anyTentative, tentativeKeys, MOVE_IDENTITY_FIELDS } from '../lib/moves';
import { enqueueAndSync, initEstimatorSync, subscribe as subscribeQueue, queueStats, retryFailed, isNetworkError } from '../lib/estimatorQueue';
import { stashShot, updateShotTags, removeShot, clearShots, loadShots } from '../lib/cameraStash';
import { formatPhone, phoneDigits, phonesOf } from '../lib/phone';
import { groupClientsForOrder, recentlyCompletedWalkthroughs, completedStamp, RECENT_WT_DAYS } from '../lib/clientOrder';
import PhonesEditor, { normalizePhonesForSave } from '../components/forms/PhonesEditor';
import { useConfirm } from '../hooks/useConfirm';
import { useToast } from '../hooks/useToast';
import { useAutosave } from '../hooks/useAutosave';
import {
    NumberField, Switch, Slider, Button, IconButton, ChoiceKeys,
    MobileScreen, MobileHeader, MobileHeaderButton, MobileTabBar, MobileActionBar, MobileCard, mobileInputCls,
    SearchInput, FilterChips, EmptyState, StatusPill, AutosaveStatus,
    TripStrip, MoveSwitcher,
    listPanelCls, listRowCls, listGroupCls,
} from '../components/ui';
import { IconPlus, IconChevronRight, IconPhone, IconEdit, IconLogOut, IconClipboard, IconFileText, IconX } from '../components/icons';

// Walkthrough status → header / row pill (mobile.md: In progress amber,
// Done emerald, Not started slate).
const WT_STATUS_PILL = {
    not_started: { label: 'Not started', color: '#64748b' },
    in_progress: { label: 'In progress', color: '#d97706' },
    completed: { label: 'Done', color: '#059669' },
};
const wtPill = (status) => WT_STATUS_PILL[status] || WT_STATUS_PILL.not_started;

// "HH:MM" (24h) → "2:30 PM"; '' when unparseable.
const fmtTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h)) return '';
    return `${h % 12 === 0 ? 12 : h % 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
// "YYYY-MM-DD" → "Today" / "Sep 18" (noon-anchored so the local date never
// slips a day).
const fmtDayShort = (ymd, today) => {
    if (!ymd) return '';
    if (ymd === today) return 'Today';
    return new Date(ymd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Inline camera glyph for the Photos tab (no camera icon in ../icons).
const IconCameraGlyph = ({ size = 20, className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
);

const ROOM_TAGS = ['Living Room', 'Master Bedroom', 'Bedroom 2', 'Bedroom 3', 'Kitchen', 'Dining Room', 'Bathroom', 'Office', 'Garage', 'Basement', 'Attic', 'Outdoor', 'Storage', 'Hallway', 'Other'];

// Velocity threshold for swipe-to-navigate (px/ms). Mirrors the desktop
// PhotoGallery — anything above this counts as a flick and triggers the
// transition regardless of distance. Distance fallback (60px) covers
// slower deliberate drags.
const SWIPE_VELOCITY = 0.11;
const SWIPE_DISTANCE = 60;

// Sync chip — surfaces the estimatorQueue state in the header. Hidden when
// everything's synced; amber count while writes are parked (offline);
// tappable red when something failed for a non-network reason.
function SyncChip() {
    const [stats, setStats] = useState({ pending: 0, failed: 0, total: 0 });
    useEffect(() => subscribeQueue(snap => setStats(queueStats(snap))), []);
    if (!stats.total) return null;
    if (stats.failed > 0) {
        return (
            <MobileHeaderButton label="Retry failed syncs" onClick={retryFailed} className="!bg-red-600/90 text-[12px]">
                {stats.failed} failed · retry
            </MobileHeaderButton>
        );
    }
    return (
        <span className="h-10 inline-flex items-center text-[12px] font-bold bg-amber-300/90 text-amber-950 px-2.5 rounded-lg flex-shrink-0" title="Saved on this phone — will sync when back online">
            {stats.pending} to sync
        </span>
    );
}

function WalkthroughPortal({ onExit }) {
    const { clients, jobs, moves, offlineSnapshotAt } = useStore();
    const { signOut } = useAuth();
    const [view, setView] = useState('clients');
    const [selectedClient, setSelectedClient] = useState(null);
    const [search, setSearch] = useState('');

    // Offline write queue — wire the flush triggers once per mount.
    useEffect(() => { initEstimatorSync(); }, []);

    // Grouped by walkthrough status so an estimator can jump back to the
    // client they just worked on (Jaylen 2026-07-01): In Progress → Recently
    // Completed → Scheduled → everyone else. Searching collapses to a flat
    // newest-first list (you're hunting for one specific person).
    // Walkthrough TIME lives on the WALKTHROUGH move_jobs row, not the
    // client — join it in so same-day appointments order by appointment
    // time (Julian 2026-07-02: his walking-in-now client sank mid-list).
    const wtTimeByClient = useMemo(() => {
        const m = new Map();
        (jobs || []).forEach(j => {
            if (j.type === 'WALKTHROUGH' && j.client_id) m.set(j.client_id, j.time || '');
        });
        return m;
    }, [jobs]);

    // Chip filter over the unified groups (2026-08-05 reorder).
    const [listFilter, setListFilter] = useState('all');

    // Unified ordering (src/lib/clientOrder.js, 2026-08-05) — same
    // buckets as the desktop Clients page: Today's walkthroughs (in
    // appointment order, in-progress pinned) → Upcoming walkthroughs →
    // Moves coming up → Needs attention → Completed tail. Searching
    // still collapses to a flat newest-first list.
    const { flat, groups, counts } = useMemo(() => {
        const q = search.toLowerCase().trim();
        const qDigits = phoneDigits(search);
        const phoneHit = (c) => qDigits.length >= 3 && (
            phoneDigits(c.phone).includes(qDigits) ||
            (Array.isArray(c.phones) && c.phones.some(p => phoneDigits(p?.number).includes(qDigits)))
        );
        const matched = clients.filter(c =>
            !q || (c.name || '').toLowerCase().includes(q) || phoneHit(c) || (c.origin_address || '').toLowerCase().includes(q)
        );
        // Chip counts come from the UNFILTERED grouping (header subline +
        // chips describe the whole list, not the current slice).
        const all = groupClientsForOrder(clients, jobs);
        const sizeOf = (key) => (all.find(g => g.key === key)?.items.length || 0);
        // Recently completed (Jaylen 2026-09-19: "add tab to walkthrough app
        // for recently completed walkthroughs") — shared cross-cut in
        // clientOrder.js, same list as the desktop Clients page chip.
        const recent = recentlyCompletedWalkthroughs(clients);
        const counts = { all: clients.length, today: sizeOf('today'), upcoming: sizeOf('upcoming_wt'), attention: sizeOf('attention'), completed: recent.length };
        if (q) return { flat: [...matched].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')), groups: null, counts };

        let built = all;
        if (listFilter === 'today') built = built.filter(g => g.key === 'today');
        else if (listFilter === 'scheduled') built = built.filter(g => g.key === 'today' || g.key === 'upcoming_wt');
        else if (listFilter === 'attention') built = built.filter(g => g.key === 'attention');
        else if (listFilter === 'completed') built = recent.length ? [{ key: 'recent_completed', label: `Completed · last ${RECENT_WT_DAYS} days`, items: recent }] : [];
        return { flat: null, groups: built, counts };
    }, [clients, search, jobs, listFilter]);

    if (view === 'new-client') {
        return (
            <WalkthroughClientIntake
                onBack={() => setView('clients')}
                onSaved={(saved) => {
                    // Brand-new client just got created — drop straight into
                    // the walkthrough capture flow for them so the estimator
                    // can start photographing/taking notes immediately.
                    setSelectedClient(saved);
                    setView('walkthrough');
                }}
            />
        );
    }

    if (view === 'walkthrough' && selectedClient) {
        return <WalkthroughCapture client={selectedClient} onBack={() => { setView('clients'); setSelectedClient(null); }} />;
    }

    const today = new Date().toLocaleDateString('en-CA');

    // One ruled-list row: name / one meta line (when · address) / status
    // pill / chevron (mobile.md "Lists").
    const ClientRow = (client) => {
        const ws = client.walkthrough_status || 'not_started';
        // Scheduled walkthroughs show their appointment (Today 2:30 PM) —
        // the thing the estimator is actually driving to. Everyone else
        // falls back to the upcoming move date, read through the client's
        // CURRENT MOVE (2026-09-18) so a tentative date wears its "~"
        // (fmtMoveDate). Still future-only, as the old jobs-based lookup was.
        let when = '';
        const mv = currentMoveFor(client, moves);
        const loadDate = mv ? mv.load_date : client.move_date;
        const loadTent = mv ? !!mv.load_tentative : !!client.date_tentative;
        if (listFilter === 'completed' && !search.trim() && ws === 'completed') {
            // Recently completed list: the completion stamp is the point.
            when = completedStamp(client, { todayIso: today });
        } else if (client.walkthrough_date && ws !== 'completed') {
            when = [fmtDayShort(client.walkthrough_date, today), fmtTime12(wtTimeByClient.get(client.id))].filter(Boolean).join(' ');
        } else if (loadDate && loadDate >= today) {
            when = `Move ${loadDate === today ? `${loadTent ? '~' : ''}Today` : fmtMoveDate(loadDate, { tentative: loadTent })}`;
        }
        const meta = [when, client.origin_address].filter(Boolean).join(' · ');
        const pill = wtPill(ws);
        return (
            <div
                key={client.id}
                role="button"
                tabIndex={0}
                onClick={() => { setSelectedClient(client); setView('walkthrough'); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedClient(client); setView('walkthrough'); } }}
                className={`${listRowCls} flex items-center gap-3 px-3 min-h-[56px] py-2 ${ws === 'completed' && listFilter !== 'completed' ? 'opacity-70' : ''}`}
            >
                <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-bold text-slate-900 truncate leading-tight">{client.name || 'Unnamed'}</div>
                    {meta && <div className="text-[12px] text-slate-500 truncate mt-0.5">{meta}</div>}
                </div>
                <StatusPill label={pill.label} color={pill.color} />
                <IconChevronRight size={18} className="text-slate-300 flex-shrink-0" />
            </div>
        );
    };

    const empty = flat ? flat.length === 0 : (!groups || groups.length === 0);

    return (
        <MobileScreen
            header={(
                <MobileHeader
                    title="Walkthroughs"
                    sub={`${counts.today} today · ${counts.upcoming} upcoming`}
                    right={(
                        <>
                            <SyncChip />
                            <MobileHeaderButton label="Open the office app" onClick={onExit}>CRM</MobileHeaderButton>
                            <MobileHeaderButton label="Log out" onClick={signOut}><IconLogOut size={18} /></MobileHeaderButton>
                        </>
                    )}
                />
            )}
            action={(
                <MobileActionBar>
                    <Button size="lg" full leftIcon={<IconPlus size={18} />} onClick={() => setView('new-client')}>New client</Button>
                </MobileActionBar>
            )}
        >
            {/* Offline snapshot banner (2026-08-26 offline pass): the store
                couldn't reach Supabase and is showing the last-synced copy.
                Edits still work — they park in the estimatorQueue and sync
                automatically when signal returns. The one inline banner the
                phone apps allow (mobile.md). */}
            {offlineSnapshotAt && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-[12px] font-semibold px-3 py-2 text-center">
                    No signal — showing data synced {(() => {
                        try { return new Date(offlineSnapshotAt).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }); }
                        catch { return 'earlier'; }
                    })()} · edits queue and send when you're back online
                </div>
            )}

            <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search clients…"
                ariaLabel="Search clients"
                className="[&>input]:h-12 [&>input]:text-[15px]"
            />

            {/* Quick filters over the unified groups (hidden while
                searching — search is already a filter). */}
            {!search.trim() && (
                <FilterChips
                    scroll
                    ariaLabel="Filter clients"
                    value={listFilter}
                    onChange={setListFilter}
                    items={[
                        { key: 'all', label: 'All', count: counts.all },
                        { key: 'today', label: 'Today', count: counts.today },
                        { key: 'scheduled', label: 'Scheduled', count: counts.today + counts.upcoming },
                        { key: 'attention', label: 'Needs walkthrough', count: counts.attention },
                        { key: 'completed', label: 'Recent Walkthroughs', count: counts.completed },
                    ]}
                />
            )}

            {/* Client list — one ruled panel, grouped by walkthrough status
                (flat when searching). */}
            {empty ? (
                <EmptyState
                    title={search.trim() ? 'No clients match' : listFilter === 'completed' ? 'Nothing completed recently' : listFilter !== 'all' ? 'Nothing here' : 'No clients yet'}
                    hint={search.trim() ? 'Try a name, phone number or address.' : listFilter === 'completed' ? `Walkthroughs marked Complete in the last ${RECENT_WT_DAYS} days show up here.` : listFilter !== 'all' ? 'Try another filter.' : 'Add the first one with New client below.'}
                />
            ) : (
                <div className={listPanelCls}>
                    {flat ? flat.map(ClientRow) : groups.map(g => (
                        <React.Fragment key={g.key}>
                            <div className={listGroupCls}>{g.label} ({g.items.length})</div>
                            {g.items.map(ClientRow)}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </MobileScreen>
    );
}

// ===== WALKTHROUGH CAPTURE SCREEN =====
function WalkthroughCapture({ client, onBack }) {
    const { patchClient, jobs, clients, moves, saveMove, setCurrentMove, confirmMoveDates } = useStore();
    const { user, displayName } = useAuth();
    // The list hands us a snapshot; the live row is what the store keeps
    // current (current_move_id flips on a switch, mirrored columns follow
    // every move save). The CURRENT MOVE is the record the Details tab
    // edits (moves-architecture.md §3 rule 1, 2026-09-18).
    const liveClient = (clients || []).find(c => c.id === client.id) || client;
    const move = currentMoveFor(liveClient, moves);
    const clientMoves = movesForClient(moves, client.id);
    const { show } = useToast();
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const [tab, setTab] = useState('details');
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Explicit walkthrough status (sql/064): not_started → in_progress → completed.
    // The estimator drives it via the pinned action bar; the office sees
    // it (+ who/when) on the client file, and the lead pipeline's Review queue
    // keys off 'completed'.
    const estimatorName = (() => {
        const raw = (user?.email || '').split('@')[0] || 'Estimator';
        return raw.charAt(0).toUpperCase() + raw.slice(1);
    })();
    const [wtStatus, setWtStatus] = useState(client.walkthrough_status || 'not_started');
    const [wtInfo, setWtInfo] = useState({
        started_by: client.walkthrough_started_by || null,
        completed_by: client.walkthrough_completed_by || null,
        completed_at: client.walkthrough_completed_at || null,
    });
    const [completing, setCompleting] = useState(false);

    // Details form seed. Identity fields (route, access, packing flag,
    // heavy items) come from the CURRENT MOVE when there is one — that is
    // the row they save to (2026-09-18); the client's mirror columns are
    // the fallback for a client with no move row. furniture_class /
    // packing_notes are client-only. Dates are NOT here any more: the
    // TripStrip writes them straight to the move, no debounce.
    const detailsFrom = (c, mv) => {
        const src = mv || c;
        return {
            origin_address: src.origin_address || '',
            destination_address: src.destination_address || '',
            additional_stops: src.additional_stops || '',
            // Furniture quality tier (sql/048) — the estimator judges this in
            // the home; informs crew/care allocation and pricing conversations.
            furniture_class: c.furniture_class || '',
            // Real packing value ('none'/'partial'/'full'/'tbd') — matches the
            // office. (Was a boolean that collapsed partial/full to yes and lost
            // the distinction on save. Fixed 2026-07-01.)
            packing_needed: src.packing_needed || 'tbd',
            packing_notes: c.packing_notes || '',
            // Force booleans so the toggle UI stays consistent even if a row
            // somehow comes back with a truthy non-boolean (older data drift).
            from_stairs: !!src.from_stairs,
            to_stairs: !!src.to_stairs,
            from_elevator: !!src.from_elevator,
            to_elevator: !!src.to_elevator,
            from_sqft: src.from_sqft || '',
            to_sqft: src.to_sqft || '',
            access_notes: src.access_notes || '',
            heavy_items: src.heavy_items || '',
            // walkthrough_notes / special_notes retired 2026-07-01 — the estimator
            // now writes the unified Office/Crew notes (ClientNotesPanel, same rows
            // as desktop). Existing values migrated into Office Notes (sql/066).
        };
    };
    const [details, setDetails] = useState(() => detailsFrom(client, move));

    // Office toasts (feedback.md 1B) behind the (msg, isError) signature the
    // photo tab already calls.
    const showToast = (msg, isError = false) => show(msg, isError ? 'error' : 'success');

    // ── Offline-tolerant client patch (estimatorQueue) ─────────────────
    // Try the live patch first (keeps the store's client row fresh); on a
    // NETWORK failure park it in the durable queue and report 'offline' —
    // the write survives app close and syncs when signal returns. Real
    // rejections still throw.
    const patchWithQueue = async (key, patch, label) => {
        try {
            await patchClient(client.id, patch);
            return 'synced';
        } catch (e) {
            if (!isNetworkError(e)) throw e;
            await enqueueAndSync({ key, kind: 'patch_client', args: { id: client.id, patch }, label });
            return 'offline';
        }
    };

    // ── Contact editor (Jaylen 2026-07-29: Jensen collected a client's
    // email ON the estimate and had no way to enter it) ────────────────
    // Tap the name in the header → bottom sheet with name / phones /
    // email. Reuses PhonesEditor (sql/068 multi-phone) and writes the
    // same shape the desktop intake writes (phones[] + mirrored primary
    // in client.phone), through the offline-tolerant patch queue.
    const [contactOpen, setContactOpen] = useState(false);
    const [contactForm, setContactForm] = useState(null);
    const [contactSaving, setContactSaving] = useState(false);
    const openContact = () => {
        setContactForm({
            name: client.name || '',
            email: client.email || '',
            phones: phonesOf(client).map(p => ({ ...p, number: formatPhone(p.number) })),
        });
        setContactOpen(true);
    };
    const saveContact = async () => {
        const name = (contactForm.name || '').trim();
        if (!name) { showToast('Name required', true); return; }
        const phones = normalizePhonesForSave(contactForm.phones);
        setContactSaving(true);
        try {
            const patch = {
                name,
                email: (contactForm.email || '').trim(),
                phones,
                phone: phones.find(p => p.is_primary)?.number || '',
            };
            const result = await patchWithQueue('contact_info', patch, 'Contact info');
            setContactOpen(false);
            showToast(result === 'offline'
                ? 'No signal — queued, will send when you\'re back online'
                : 'Contact info saved');
        } catch (e) {
            console.error('Contact save error:', e);
            showToast('Save failed', true);
        } finally {
            setContactSaving(false);
        }
    };

    // ── Details save — LIFTED from the tab (Jaylen 2026-07-02) ─────────
    // The one save path for the Details tab: useAutosave (feedback.md 5C)
    // calls it ~1.2s after the last edit, Back / tab switches flush it.
    // savedDetailsRef holds the last-persisted snapshot. With the offline
    // queue underneath, "save" can't strand the estimator — worst case the
    // data parks on the phone and syncs later.
    const savedDetailsRef = useRef(JSON.stringify(details));
    // Re-seed the form when the CURRENT MOVE changes under us (the
    // MoveSwitcher, or the office flipping it). Done during render — not
    // in an effect — so the keyed DetailsTab / RoutePointsEditor below
    // mount with the new move's addresses, never a frame of the old ones
    // (the route editor seeds its stops once on mount). 2026-09-18.
    const [seededMoveId, setSeededMoveId] = useState(move?.id || null);
    if ((move?.id || null) !== seededMoveId) {
        const next = detailsFrom(liveClient, move);
        setSeededMoveId(move?.id || null);
        setDetails(next);
        savedDetailsRef.current = JSON.stringify(next);
    }
    const detailsDirty = JSON.stringify(details) !== savedDetailsRef.current;
    // Wire shape of the form (what the row actually receives).
    const normalizeDetails = (d) => ({
        ...d,
        furniture_class: d.furniture_class || null,
        // Sq ft can't be negative (QA M4 OBS-9).
        from_sqft: Math.max(0, parseInt(d.from_sqft) || 0),
        to_sqft: Math.max(0, parseInt(d.to_sqft) || 0),
    });
    const saveDetails = async () => {
        if (!detailsDirty) return true;
        try {
            const payload = normalizeDetails(details);
            // Only the keys that changed since the last save go out, split
            // by owner (moves-architecture.md rule 1, 2026-09-18): identity
            // keys (route, access, packing flag, heavy items —
            // MOVE_IDENTITY_FIELDS) → saveMove on the CURRENT MOVE, which
            // also keeps the in-memory move + mirrors fresh; furniture /
            // packing notes → the client. A client with no move row sends
            // everything to the client as before.
            let prev = {};
            try { prev = JSON.parse(savedDetailsRef.current || '{}'); } catch { prev = {}; }
            const changedKeys = Object.keys(payload).filter(k => JSON.stringify(details[k]) !== JSON.stringify(prev[k]));
            const movePatch = {}, clientPatch = {};
            changedKeys.forEach(k => { (move?.id && MOVE_IDENTITY_FIELDS.includes(k) ? movePatch : clientPatch)[k] = payload[k]; });
            let outcome = 'synced';
            try {
                if (Object.keys(movePatch).length) await saveMove({ id: move.id, ...movePatch });
                if (Object.keys(clientPatch).length) await patchClient(client.id, clientPatch);
            } catch (e) {
                if (!isNetworkError(e)) throw e;
                // Offline: the durable queue only speaks client patches, and
                // a same-key enqueue REPLACES the parked copy — so park the
                // WHOLE form, exactly as before. db.patchClient redirects
                // the identity keys to the current move when it replays.
                await enqueueAndSync({ key: `details:${client.id}`, kind: 'patch_client', args: { id: client.id, patch: payload }, label: 'Walkthrough details' });
                outcome = 'offline';
            }
            savedDetailsRef.current = JSON.stringify(details);
            ensureStarted();
            // No toast on autosave (5C) — the status line under the cards
            // is the indicator. The offline case is the one banner-worthy
            // message, and it goes through the toast so it's seen.
            if (outcome === 'offline') showToast('No signal — queued, will send when you\'re back online');
            return true;
        } catch (e) {
            // Short copy on the status line; the raw Postgres text goes to
            // the console for debugging (QA M4 OBS-5).
            console.error('Details save error:', e);
            return false;
        }
    };

    // ── Move dates (TripStrip → saveMove, no debounce) ──────────────────
    // A date lands on the move the moment it's tapped (2026-09-18).
    // pendingDates keeps an offline tap visible on the strip until the
    // queued write replays (estimatorQueue kind `upsert_move`; one queue
    // key per move so repeated taps collapse to the latest full patch).
    const [pendingDates, setPendingDates] = useState({});
    const dropPending = (keys) => setPendingDates(p => { const n = { ...p }; keys.forEach(k => { delete n[k]; }); return n; });
    const pendingDatesRef = useRef({});
    const saveDates = async (patch) => {
        if (!move?.id || !patch || !Object.keys(patch).length) return;
        setPendingDates(p => ({ ...p, ...patch }));
        try {
            await saveMove({ id: move.id, ...patch });
            dropPending(Object.keys(patch));
            ensureStarted();
        } catch (e) {
            if (!isNetworkError(e)) {
                console.error('Move date save error:', e);
                dropPending(Object.keys(patch));
                showToast('Couldn\'t save the date', true);
                return;
            }
            // Offline: park the accumulated date patch for this move. A
            // same-key enqueue replaces the parked copy, so merge every
            // offline tap into one item rather than losing earlier keys.
            pendingDatesRef.current = { ...pendingDatesRef.current, ...patch };
            await enqueueAndSync({ key: `dates:${move.id}`, kind: 'upsert_move', args: { id: move.id, ...pendingDatesRef.current }, label: 'Move dates' });
            showToast('No signal — queued, will send when you\'re back online');
        }
    };

    // Confirm every tentative date on the move, logged under the
    // estimator's name (confirmMoveDates appends to date_confirmations —
    // a log, not a lock; rule 5). The auth user carries no `name`, so the
    // email-derived estimatorName is the fallback, same as the
    // walkthrough_started_by stamp.
    const [confirming, setConfirming] = useState(false);
    const confirmAllDates = async () => {
        if (!move?.id) return;
        const keys = tentativeKeys(move);
        if (!keys.length) return;
        const by = displayName || user?.name || estimatorName;
        setConfirming(true);
        try {
            await confirmMoveDates(move.id, keys, by);
            showToast(`Dates confirmed · logged as ${by}`);
        } catch (e) {
            console.error('Confirm dates error:', e);
            showToast(isNetworkError(e) ? 'No signal — confirm the dates when you\'re back online' : 'Couldn\'t confirm the dates', true);
        } finally {
            setConfirming(false);
        }
    };
    // Details autosave — `save` returns true or a short error string; the
    // tab's `set` calls `touch()` after every edit. `enabled` is always on:
    // an existing record autosaves (new clients go through the intake's
    // explicit Save).
    const autosave = useAutosave({
        save: async () => ((await saveDetails()) ? true : 'check the fields'),
        enabled: true,
    });
    const { flush: flushDetails } = autosave;

    // MoveSwitcher (3B) on the phone: pending edits belong to the move on
    // screen, so flush them first, then flip the client's current move
    // (the form re-seeds from the new move during render, above). No
    // "+ New move" here — that's an office action. 2026-09-18.
    const switchMove = async (m) => {
        if (!m?.id || m.id === move?.id) return;
        const ok = await flushDetails();
        if (!ok) { showToast('Fix the details that didn\'t save before switching moves', true); return; }
        try {
            await setCurrentMove(client.id, m.id);
            setPendingDates({});
        } catch (e) {
            console.error('Switch move failed:', e);
            showToast('Couldn\'t switch moves', true);
        }
    };

    const handleBack = async () => {
        // Flush a pending edit through the same save; only an UNSAVEABLE
        // edit asks before leaving (feedback.md 5C).
        const ok = await flushDetails();
        if (!ok) {
            const leave = await confirmDialog({
                title: `Leave ${client.name || 'this client'}?`,
                body: 'Some details couldn\'t be saved. Leave anyway and lose those edits, or stay and fix them.',
                confirmLabel: 'Leave anyway',
                cancelLabel: 'Stay',
                danger: true,
            });
            if (!leave) return;
        }
        onBack();
    };

    // First time ANY walkthrough work lands (photo, details, notes), flip the
    // status to 'in_progress' if it hasn't been started/completed yet — so the
    // office sees "In progress" even if the estimator never taps Begin. Does
    // NOT mark complete (that's an explicit action now, sql/064). First-write-only.
    const startedRef = useRef(client.walkthrough_status === 'in_progress' || client.walkthrough_status === 'completed');
    const ensureStarted = async () => {
        if (startedRef.current) return;
        startedRef.current = true;
        try {
            const now = new Date().toISOString();
            await patchWithQueue(`wtstatus:${client.id}`, { walkthrough_status: 'in_progress', walkthrough_started_at: now, walkthrough_started_by: estimatorName }, 'Walkthrough status');
            setWtStatus('in_progress');
            setWtInfo(i => ({ ...i, started_by: estimatorName }));
            // Announce the auto-begin (QA M4 OBS-1, Jaylen's call: keep the
            // "activity implies begun" behavior but never change status
            // silently).
            showToast('Walkthrough started');
        } catch (e) {
            startedRef.current = false; // retry on the next save action
            console.warn('walkthrough start stamp failed:', e);
        }
    };

    // Explicit "Mark Complete" — this is what advances the client to the office
    // "Awaiting Brothers' Review" queue (replaced the old ~30-min timer).
    const handleComplete = async () => {
        setCompleting(true);
        try {
            const now = new Date().toISOString();
            const outcome = await patchWithQueue(`wtstatus:${client.id}`, {
                walkthrough_status: 'completed',
                walkthrough_completed_at: now,
                walkthrough_completed_by: estimatorName,
                walkthrough_started_by: wtInfo.started_by || estimatorName,
            }, 'Walkthrough status');
            startedRef.current = true;
            setWtStatus('completed');
            setWtInfo(i => ({ ...i, completed_by: estimatorName, completed_at: now }));
            // Reversible → Undo on the toast (feedback.md 1B) runs the same
            // reopen handler as the pinned Reopen button.
            show('Walkthrough complete', 'success', {
                undo: handleReopen,
                description: outcome === 'offline' ? 'Queued — will send when you\'re back online' : undefined,
            });
        } catch (e) { console.error('Mark complete failed:', e); showToast('Couldn\'t mark complete', true); }
        finally { setCompleting(false); }
    };
    const handleReopen = async () => {
        try {
            await patchWithQueue(`wtstatus:${client.id}`, { walkthrough_status: 'in_progress', walkthrough_completed_at: null, walkthrough_completed_by: null }, 'Walkthrough status');
            setWtStatus('in_progress');
            setWtInfo(i => ({ ...i, completed_by: null, completed_at: null }));
        } catch (e) { console.error('Reopen failed:', e); showToast('Couldn\'t reopen', true); }
    };

    useEffect(() => {
        async function load() {
            try {
                const p = await db.getPhotos(client.id);
                setPhotos(p || []);
            } catch (e) { console.error('Failed to load photos:', e); }
            finally { setLoading(false); }
        }
        load();
    }, [client.id]);

    const tabItems = [
        { key: 'photos', label: photos.length ? `Photos (${photos.length})` : 'Photos', icon: <IconCameraGlyph size={22} /> },
        { key: 'details', label: 'Details', icon: <IconClipboard size={22} /> },
        { key: 'notes', label: 'Notes', icon: <IconFileText size={22} /> },
    ];
    const switchTab = (key) => {
        // Leaving Details flushes a pending autosave (fire-and-forget; the
        // offline queue backstops it).
        if (tab === 'details' && key !== 'details') flushDetails();
        setTab(key);
    };

    // Header subline: pickup street · walkthrough appointment · move dates.
    // The appointment time lives on the WALKTHROUGH move_jobs row, the
    // date on the client. Move dates read through the CURRENT MOVE
    // (2026-09-18): "Move ~Sep 16 → Unload Sep 20" — fmtMoveDate puts the
    // "~" on a tentative date. Street only for the address (the full one
    // is in the Route card) so the dates fit the one truncated line.
    const headerSub = (() => {
        const today = new Date().toLocaleDateString('en-CA');
        const wtJob = (jobs || []).find(j => j.type === 'WALKTHROUGH' && j.client_id === client.id);
        const when = liveClient.walkthrough_date
            ? [fmtDayShort(liveClient.walkthrough_date, today), fmtTime12(wtJob?.time)].filter(Boolean).join(' ')
            : '';
        const loadDate = move ? move.load_date : liveClient.move_date;
        const loadTent = move ? !!move.load_tentative : !!liveClient.date_tentative;
        let dates = loadDate ? `Move ${fmtMoveDate(loadDate, { tentative: loadTent })}` : '';
        if (dates && move?.unload_date) dates += ` → Unload ${fmtMoveDate(move.unload_date, { tentative: move.unload_tentative, windowEnd: move.unload_window_end })}`;
        const street = (liveClient.origin_address || '').split(',')[0].trim();
        return [street || 'No address', when, dates].filter(Boolean).join(' · ');
    })();
    const pill = wtPill(wtStatus);

    return (
        <MobileScreen
            header={(
                <MobileHeader
                    title={liveClient.name}
                    status={pill}
                    sub={headerSub}
                    onBack={handleBack}
                    right={(
                        <>
                            <SyncChip />
                            {/* Edit contact info (name / phones / email) — the bottom sheet below. */}
                            <MobileHeaderButton label="Edit contact info" onClick={openContact}><IconEdit size={18} /></MobileHeaderButton>
                            {client.phone && (
                                <MobileHeaderButton label="Call client" href={`tel:${client.phone}`}><IconPhone size={18} /></MobileHeaderButton>
                            )}
                        </>
                    )}
                />
            )}
            tabs={<MobileTabBar items={tabItems} value={tab} onChange={switchTab} />}
            action={(
                // One pinned action: Begin → Mark complete (with Undo) →
                // ✓ Completed + Reopen. "Mark complete" is what moves the
                // client into the office's Brothers'-Review queue (sql/064).
                <MobileActionBar>
                    {wtStatus === 'completed' ? (
                        <>
                            <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-bold text-emerald-700">✓ Completed</div>
                                <div className="text-[11px] text-slate-400 truncate">{wtInfo.completed_by ? `by ${wtInfo.completed_by}` : ''}{wtInfo.completed_at ? ` · ${new Date(wtInfo.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</div>
                            </div>
                            <Button variant="tertiary" size="sm" onClick={handleReopen}>Reopen</Button>
                        </>
                    ) : wtStatus === 'in_progress' ? (
                        <Button variant="success" size="lg" full loading={completing} onClick={handleComplete}>Mark complete</Button>
                    ) : (
                        <Button size="lg" full onClick={ensureStarted}>Begin walkthrough</Button>
                    )}
                </MobileActionBar>
            )}
        >
            {tab === 'photos' && <PhotoCaptureTab clientId={client.id} photos={photos} setPhotos={setPhotos} showToast={showToast} loading={loading} onActivity={ensureStarted} />}
            {/* Keyed by the current move so the tab (and the route editor
                it seeds once on mount) re-hydrates when the switcher
                changes moves. */}
            {tab === 'details' && (
                <DetailsTab
                    key={move?.id || 'no-move'}
                    details={details} setDetails={setDetails} onEdit={autosave.touch} autosave={autosave}
                    move={move} moves={clientMoves} pendingDates={pendingDates}
                    onSwitchMove={switchMove} onDates={saveDates} onConfirmAll={confirmAllDates} confirming={confirming}
                />
            )}
            {tab === 'notes' && <NotesTab client={client} onActivity={ensureStarted} />}

            {confirmElement}

            {/* Contact editor bottom sheet — portal'd so nothing in the
                shell can clip it. Same hand-rolled overlay pattern as the
                in-app camera (this page doesn't use the desktop <Modal>). */}
            {contactOpen && contactForm && createPortal((
                <div className="theme-fixed fixed inset-0 z-[220] bg-black/50 flex items-end" onClick={(e) => { if (e.target === e.currentTarget) setContactOpen(false); }}>
                    <div className="w-full bg-white rounded-t-xl px-4 pt-3 space-y-3 max-h-[85vh] overflow-y-auto text-slate-900" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-[16px] text-slate-900">Contact info</h3>
                            <IconButton label="Close" size="lg" onClick={() => setContactOpen(false)}><IconX size={20} /></IconButton>
                        </div>
                        <div>
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Name *</label>
                            <input value={contactForm.name}
                                onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))}
                                className={`mt-1 ${mobileInputCls}`} />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Phone numbers</label>
                            <div className="mt-1">
                                <PhonesEditor
                                    value={contactForm.phones}
                                    onChange={(rows) => setContactForm(f => ({ ...f, phones: rows }))}
                                    inputCls={mobileInputCls}
                                    compact />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Email</label>
                            <input type="email" inputMode="email" autoCapitalize="none" value={contactForm.email}
                                onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                                placeholder="client@example.com"
                                className={`mt-1 ${mobileInputCls}`} />
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button variant="secondary" className="flex-1" onClick={() => setContactOpen(false)} disabled={contactSaving}>Cancel</Button>
                            <Button className="flex-1" onClick={saveContact} loading={contactSaving}>Save</Button>
                        </div>
                    </div>
                </div>
            ), document.body)}
        </MobileScreen>
    );
}

// ===== IN-APP RAPID CAMERA (v2, 2026-07-02) =====
// Fullscreen getUserMedia viewfinder built for walkthrough documentation:
//   • Rapid shutter — shots pile into the strip, "Use N Photos" hands them
//     to the normal handleFiles pipeline (2400px downscale happens there).
//   • Capture controls, all FEATURE-DETECTED from the video track (iOS
//     Safari exposes almost none of these — controls hide, never break):
//     torch, tap-to-focus (reticle), hardware zoom (slider + pinch).
//     No digital-zoom fallback by design — it costs resolution on
//     liability shots (Jaylen 2026-07-02).
//   • Tag-as-you-shoot: arm a room tag (ROOM_TAGS + free-text custom) and
//     every shot carries it as METADATA (room_tags[]) — never burned into
//     pixels. Editable per-shot in review.
//   • Shot review: tap a thumbnail to inspect fullscreen (pinch-zoom),
//     re-tag, or delete (two-tap confirm) — replaces v1's instant
//     tap-to-delete.
//   • Letterboxed preview (object-contain): the estimator sees the FULL
//     sensor frame, so nothing lands in the save that wasn't on screen.
//   • Durable shots: every capture stashes to IndexedDB (cameraStash) and
//     restores on reopen — killing the app can't eat photos. Stash clears
//     when shots are handed off; closing with ✕ keeps them for next time.
// Fallback: if the camera can't start (denied / unsupported), onFallback()
// opens the native file-input camera — never worse than before.
function InAppCamera({ clientId, onDone, onFallback }) {
    const videoRef = useRef(null);
    const viewRef = useRef(null);
    const streamRef = useRef(null);
    const trackRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [shots, setShots] = useState([]); // { id, file, url, tags }
    const [flash, setFlash] = useState(false);

    // Capability-driven controls
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchOn, setTorchOn] = useState(false);
    const [zoomCaps, setZoomCaps] = useState(null); // { min, max, step }
    const [zoomValue, setZoomValue] = useState(1);
    const [focusSupported, setFocusSupported] = useState(false);
    const [reticle, setReticle] = useState(null); // { x, y } in view px

    // Tag-as-you-shoot
    const [armedTag, setArmedTag] = useState('');
    const [customOpen, setCustomOpen] = useState(false);
    const [customTag, setCustomTag] = useState('');

    // Review overlay
    const [review, setReview] = useState(null); // shot object
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [reviewScale, setReviewScale] = useState(1);

    // Slow-start hint (QA M4 OBS-6): while the permission prompt is
    // pending, getUserMedia neither resolves nor rejects — the viewfinder
    // sat on "Starting camera…" forever with no way out. After 8s we
    // offer the native-camera fallback without killing the pending
    // request (granting late still works).
    const [slowStart, setSlowStart] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const slowTimer = setTimeout(() => { if (!cancelled) setSlowStart(true); }, 8000);
        (async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 4096 },
                        height: { ideal: 2160 },
                    },
                    audio: false,
                });
                if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
                streamRef.current = stream;
                const track = stream.getVideoTracks()[0];
                trackRef.current = track;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    try { await videoRef.current.play(); } catch { /* autoplay attr covers it */ }
                }
                // Feature-detect capture controls. iOS Safari typically
                // reports none of these — that's fine, the UI hides them.
                try {
                    const caps = track.getCapabilities ? track.getCapabilities() : {};
                    setTorchSupported(!!caps.torch);
                    if (caps.zoom && typeof caps.zoom.min === 'number' && caps.zoom.max > caps.zoom.min) {
                        setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
                        const settings = track.getSettings ? track.getSettings() : {};
                        setZoomValue(typeof settings.zoom === 'number' ? settings.zoom : caps.zoom.min);
                    }
                    setFocusSupported(Array.isArray(caps.focusMode) && caps.focusMode.length > 0 && 'pointsOfInterest' in caps);
                } catch { /* capability probing is best-effort */ }
                clearTimeout(slowTimer);
                setSlowStart(false);
                setReady(true);
            } catch (e) {
                clearTimeout(slowTimer);
                console.warn('In-app camera unavailable, falling back to native:', e?.message);
                onFallback();
            }
        })();
        // Restore any stashed shots from a previous session for this client.
        (async () => {
            try {
                const stashed = await loadShots(clientId);
                if (cancelled || !stashed.length) return;
                setShots(prev => ([
                    ...stashed.map(s => ({
                        id: s.id,
                        file: new File([s.blob], `cam_${s.id}.jpg`, { type: 'image/jpeg' }),
                        url: URL.createObjectURL(s.blob),
                        tags: s.tags || [],
                    })),
                    ...prev,
                ]));
            } catch { /* stash restore is best-effort */ }
        })();
        return () => {
            cancelled = true;
            streamRef.current?.getTracks().forEach(t => t.stop());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyAdvanced = (constraint) => {
        try { trackRef.current?.applyConstraints({ advanced: [constraint] }).catch(() => {}); } catch { /* ignore */ }
    };

    const toggleTorch = () => {
        const next = !torchOn;
        setTorchOn(next);
        applyAdvanced({ torch: next });
    };

    const setZoom = (v) => {
        if (!zoomCaps) return;
        const clamped = Math.min(zoomCaps.max, Math.max(zoomCaps.min, v));
        setZoomValue(clamped);
        applyAdvanced({ zoom: clamped });
    };

    // Pinch-to-zoom on the viewfinder (hardware zoom only). Two-pointer
    // distance delta maps onto the track's zoom range.
    const pinchRef = useRef(null); // { startDist, startZoom }
    const pointersRef = useRef(new Map());
    const onViewPointerDown = (e) => {
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 2 && zoomCaps) {
            const pts = [...pointersRef.current.values()];
            pinchRef.current = { startDist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), startZoom: zoomValue };
        }
    };
    const onViewPointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 2 && pinchRef.current && zoomCaps) {
            const pts = [...pointersRef.current.values()];
            const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            if (pinchRef.current.startDist > 0) {
                setZoom(pinchRef.current.startZoom * (dist / pinchRef.current.startDist));
            }
        }
    };
    const onViewPointerUp = (e) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) pinchRef.current = null;
    };

    // Tap-to-focus: map the tap to normalized coords WITHIN the letterboxed
    // video content (object-contain), show a reticle, and ask the track to
    // refocus there where supported.
    const onViewTap = (e) => {
        if (pointersRef.current.size > 1) return; // mid-pinch
        const view = viewRef.current;
        const video = videoRef.current;
        if (!view || !video || !video.videoWidth) return;
        const rect = view.getBoundingClientRect();
        const videoAspect = video.videoWidth / video.videoHeight;
        const viewAspect = rect.width / rect.height;
        // Content box of the letterboxed video inside the container.
        let cw, ch, cx, cy;
        if (videoAspect > viewAspect) { cw = rect.width; ch = rect.width / videoAspect; cx = 0; cy = (rect.height - ch) / 2; }
        else { ch = rect.height; cw = rect.height * videoAspect; cy = 0; cx = (rect.width - cw) / 2; }
        const px = e.clientX - rect.left, py = e.clientY - rect.top;
        if (px < cx || px > cx + cw || py < cy || py > cy + ch) return; // tapped a black bar
        setReticle({ x: px, y: py });
        setTimeout(() => setReticle(null), 800);
        if (focusSupported) {
            applyAdvanced({ focusMode: 'single-shot', pointsOfInterest: [{ x: (px - cx) / cw, y: (py - cy) / ch }] });
        }
    };

    const snap = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        setFlash(true);
        setTimeout(() => setFlash(false), 120);
        const tags = armedTag ? [armedTag] : [];
        canvas.toBlob((blob) => {
            if (!blob) return;
            const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const file = new File([blob], `cam_${id}.jpg`, { type: 'image/jpeg' });
            setShots(prev => [...prev, { id, file, url: URL.createObjectURL(blob), tags }]);
            // Durable immediately — an app kill can no longer eat the shot.
            stashShot({ id, clientId, blob, tags, createdAt: Date.now() });
        }, 'image/jpeg', 0.92);
    };

    const deleteShot = (shot) => {
        try { URL.revokeObjectURL(shot.url); } catch { /* ignore */ }
        setShots(prev => prev.filter(s => s.id !== shot.id));
        removeShot(shot.id);
    };

    const retagShot = (shot, tag) => {
        const tags = shot.tags.includes(tag) ? shot.tags.filter(t => t !== tag) : [...shot.tags, tag];
        setShots(prev => prev.map(s => s.id === shot.id ? { ...s, tags } : s));
        setReview(r => (r && r.id === shot.id ? { ...r, tags } : r));
        updateShotTags(shot.id, tags);
    };

    const finish = () => {
        const entries = shots.map(s => ({ file: s.file, tags: s.tags }));
        clearShots(shots.map(s => s.id));
        shots.forEach(s => { try { URL.revokeObjectURL(s.url); } catch { /* ignore */ } });
        streamRef.current?.getTracks().forEach(t => t.stop());
        onDone(entries);
    };

    // ✕ close: shots stay stashed (restored next open) — closing the camera
    // must never destroy work. Per-shot delete lives in review.
    const close = () => {
        shots.forEach(s => { try { URL.revokeObjectURL(s.url); } catch { /* ignore */ } });
        streamRef.current?.getTracks().forEach(t => t.stop());
        onDone([]);
    };

    const armCustom = () => {
        const t = customTag.trim();
        if (t) { setArmedTag(t); setCustomOpen(false); }
    };

    const tagChip = (label, armed, onClick, key) => (
        <button key={key || label} type="button" onClick={onClick}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${armed ? 'bg-red-600 text-white' : 'bg-white/15 text-white/90 active:bg-white/30'}`}>
            {label}
        </button>
    );

    return (
        <div className="fixed inset-0 z-[300] bg-black flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }} role="dialog" aria-label="Camera">
            {/* Viewfinder — object-CONTAIN so the estimator sees the full
                sensor frame (nothing saved that wasn't on screen). */}
            <div
                ref={viewRef}
                className="relative flex-1 overflow-hidden touch-none"
                onPointerDown={onViewPointerDown}
                onPointerMove={onViewPointerMove}
                onPointerUp={onViewPointerUp}
                onPointerCancel={onViewPointerUp}
                onClick={onViewTap}
            >
                <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-contain" />
                {flash && <div className="absolute inset-0 bg-white/70 pointer-events-none" />}
                {!ready && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70 text-sm px-6 text-center">
                        <span>Starting camera…</span>
                        {slowStart && (
                            <>
                                <span className="text-white/50 text-[12px]">Waiting on the camera permission — check for a browser prompt, or:</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onFallback(); }}
                                    className="bg-white/15 border border-white/30 text-white px-4 py-2.5 rounded-xl text-[13px] font-semibold">
                                    Use phone camera instead
                                </button>
                            </>
                        )}
                    </div>
                )}
                {reticle && (
                    <div className="absolute w-14 h-14 border-2 border-yellow-300 rounded-lg pointer-events-none animate-pulse" style={{ left: reticle.x - 28, top: reticle.y - 28 }} aria-hidden="true" />
                )}
                {/* Close (top-left) · torch (top-right) */}
                <button onClick={(e) => { e.stopPropagation(); close(); }} className="absolute top-3 left-3 w-11 h-11 rounded-full bg-black/50 text-white text-xl flex items-center justify-center" aria-label="Close camera (shots are kept)">✕</button>
                <div className="absolute top-3 right-3 flex items-center gap-2">
                    {shots.length > 0 && (
                        <span className="px-3 py-1.5 rounded-full bg-black/50 text-white text-[13px] font-bold" aria-label={`${shots.length} shots taken`}>{shots.length}</span>
                    )}
                    {torchSupported && (
                        <button onClick={(e) => { e.stopPropagation(); toggleTorch(); }}
                            className={`w-11 h-11 rounded-full flex items-center justify-center text-lg ${torchOn ? 'bg-yellow-400 text-black' : 'bg-black/50 text-white'}`}
                            aria-label={torchOn ? 'Turn flashlight off' : 'Turn flashlight on'} aria-pressed={torchOn}>
                            ⚡
                        </button>
                    )}
                </div>
                {/* Hardware zoom slider (bottom of viewfinder) */}
                {zoomCaps && (
                    <div className="absolute bottom-2 left-6 right-6 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-white/70 text-[11px] font-bold">{zoomCaps.min}×</span>
                        <Slider
                            value={zoomValue}
                            onChange={n => setZoom(n)}
                            min={zoomCaps.min} max={zoomCaps.max} step={zoomCaps.step}
                            format={v => `${Number(v).toFixed(1)}×`}
                            ariaLabel="Zoom"
                            size="sm"
                            className="flex-1 [&>span]:text-white/70 [&>span]:text-[11px]"
                        />
                    </div>
                )}
            </div>

            {/* Tag-as-you-shoot — armed tag rides every subsequent shot as
                metadata. Scrollable single row; Custom opens a small input. */}
            <div className="bg-black/90 px-2 pt-2">
                {customOpen ? (
                    <div className="flex items-center gap-2 pb-1">
                        <input autoFocus value={customTag} onChange={(e) => setCustomTag(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') armCustom(); }}
                            placeholder="Custom tag…"
                            className="flex-1 px-3 py-2 rounded-lg bg-white/15 text-white placeholder:text-white/40 text-[13px] focus:outline-none" />
                        <button type="button" onClick={armCustom} className="px-3 py-2 rounded-lg bg-red-600 text-white text-[12px] font-bold">Arm</button>
                        <button type="button" onClick={() => setCustomOpen(false)} className="px-2 py-2 text-white/60 text-[12px] font-bold">✕</button>
                    </div>
                ) : (
                    <div className="flex gap-1.5 overflow-x-auto pb-1" role="radiogroup" aria-label="Room tag for new shots">
                        {tagChip('No tag', armedTag === '', () => setArmedTag(''))}
                        {ROOM_TAGS.map(t => tagChip(t, armedTag === t, () => setArmedTag(armedTag === t ? '' : t)))}
                        {armedTag && !ROOM_TAGS.includes(armedTag) && tagChip(armedTag, true, () => setArmedTag(''), '__custom_armed')}
                        {tagChip('+ Custom', false, () => { setCustomTag(''); setCustomOpen(true); }, '__custom')}
                    </div>
                )}
            </div>

            {/* Shot strip — newest first. Tap = REVIEW (not delete). */}
            {shots.length > 0 && (
                <div className="bg-black/90 px-2 pt-1.5 flex gap-1.5 overflow-x-auto">
                    {[...shots].reverse().map(s => (
                        <button key={s.id} onClick={() => { setReview(s); setConfirmDelete(false); setReviewScale(1); }} className="relative flex-shrink-0" aria-label="Review this shot">
                            <img src={s.url} alt="" className="w-14 h-14 object-cover rounded-md" />
                            {s.tags.length > 0 && (
                                <span className="absolute bottom-0.5 left-0.5 right-0.5 truncate bg-black/70 text-white text-[8px] font-semibold px-1 py-px rounded">{s.tags[0]}</span>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Controls */}
            <div className="bg-black/90 px-4 py-4 flex items-center justify-between gap-3">
                <div className="w-24 text-white/50 text-[11px]">
                    {armedTag ? <>Tagging<br /><span className="text-white font-bold">{armedTag}</span></> : ''}
                </div>
                <button
                    onClick={snap}
                    disabled={!ready}
                    aria-label="Take photo"
                    className="w-[72px] h-[72px] rounded-full border-4 border-white bg-white/20 active:bg-white/60 disabled:opacity-30 flex-shrink-0"
                />
                <button
                    onClick={finish}
                    disabled={shots.length === 0}
                    className="w-24 py-3 rounded-xl bg-red-600 text-white text-[13px] font-bold disabled:opacity-30 active:bg-red-700"
                >
                    Use {shots.length || ''} Photo{shots.length === 1 ? '' : 's'}
                </button>
            </div>

            {/* ── Shot review overlay ── */}
            {review && (
                <div className="absolute inset-0 z-10 bg-black flex flex-col" role="dialog" aria-label="Review shot">
                    <div className="flex items-center justify-between px-3 py-2.5" style={{ paddingTop: 'max(10px, env(safe-area-inset-top))' }}>
                        <button onClick={() => setReview(null)} className="px-3 py-2 rounded-lg bg-white/15 text-white text-[13px] font-bold" aria-label="Back to camera">‹ Back</button>
                        <span className="text-white/60 text-[12px]">Pinch to zoom · double-tap to reset</span>
                    </div>
                    <div className="relative flex-1 overflow-hidden flex items-center justify-center"
                        onDoubleClick={() => setReviewScale(s => (s > 1 ? 1 : 2.5))}
                        onPointerDown={onViewPointerDown}
                        onPointerUp={onViewPointerUp}
                        onPointerCancel={onViewPointerUp}
                        onPointerMove={(e) => {
                            if (!pointersRef.current.has(e.pointerId)) return;
                            pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
                            if (pointersRef.current.size === 2) {
                                const pts = [...pointersRef.current.values()];
                                const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
                                if (!pinchRef.current) pinchRef.current = { startDist: dist, startZoom: reviewScale };
                                else if (pinchRef.current.startDist > 0) {
                                    setReviewScale(Math.min(6, Math.max(1, pinchRef.current.startZoom * (dist / pinchRef.current.startDist))));
                                }
                            }
                        }}
                    >
                        <img src={review.url} alt="Captured shot" className="max-w-full max-h-full" style={{ transform: `scale(${reviewScale})`, transition: pointersRef.current.size < 2 ? 'transform 150ms' : 'none' }} />
                    </div>
                    {/* Re-tag — same vocabulary, multi-select per shot. */}
                    <div className="bg-black/90 px-2 pt-2">
                        <div className="flex gap-1.5 overflow-x-auto pb-1" aria-label="Tags on this shot">
                            {[...new Set([...ROOM_TAGS, ...review.tags])].map(t => tagChip(t, review.tags.includes(t), () => retagShot(review, t)))}
                        </div>
                    </div>
                    <div className="bg-black/90 px-4 py-3 flex items-center gap-2" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
                        <button
                            onClick={() => {
                                if (!confirmDelete) { setConfirmDelete(true); setTimeout(() => setConfirmDelete(false), 3000); return; }
                                deleteShot(review);
                                setReview(null);
                            }}
                            className={`flex-1 py-3 rounded-xl text-[13px] font-bold min-h-[44px] ${confirmDelete ? 'bg-rose-600 text-white' : 'bg-white/15 text-rose-300'}`}
                        >
                            {confirmDelete ? 'Tap again to delete' : 'Delete shot'}
                        </button>
                        <button
                            onClick={() => { deleteShot(review); setReview(null); }}
                            className="flex-1 py-3 rounded-xl bg-white/15 text-white text-[13px] font-bold min-h-[44px]"
                            title="Delete this shot and go straight back to the viewfinder"
                        >
                            Retake
                        </button>
                        <button onClick={() => setReview(null)} className="flex-1 py-3 rounded-xl bg-emerald-600 text-white text-[13px] font-bold min-h-[44px]">
                            Keep
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ===== PHOTO CAPTURE TAB =====
//
// New tagging UX (May 2026 redesign):
//   1. Upload first — no room must be picked beforehand. Upload buttons are
//      always available.
//   2. Tap a tag chip in the toolbar to enter "tag mode" for that tag.
//   3. While in tag mode, tapping any photo toggles that tag on/off in a
//      LOCAL pending state (orange ring = will be added, red ring = already
//      had it and will be kept, no ring = won't have it). Multiple photos
//      can be tagged in one mode.
//   4. Tap "Save Tags" to commit pending changes (one DB write per changed
//      photo). Tap "Cancel" to discard.
//   5. When NOT in tag mode, tapping a photo opens a fullscreen lightbox
//      with prev/next arrows and a delete button.
//
// Photos can carry multiple room_tags (text[] column added in migration
// add_room_tags_array_to_photos). The legacy room_tag string is still read
// as a fallback for old records.
// Gallery order (sql/089): sort_order asc, uploaded_at as tiebreak. Local
// failed-upload tiles carry _sortOrder (their intended slot) and no
// uploaded_at, so they sit where the retry will land.
const sortPhotos = (list) => [...list].sort((a, b) => {
    const sa = a.sort_order ?? a._sortOrder ?? Number.MAX_SAFE_INTEGER;
    const sb = b.sort_order ?? b._sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return (a.uploaded_at || '').localeCompare(b.uploaded_at || '');
});

function PhotoCaptureTab({ clientId, photos, setPhotos, showToast, loading, onActivity }) {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    // Reorder mode (Jaylen 2026-09-11): ◀ ▶ on every tile, Done persists
    // the whole client's order as 1..n (db.reorderPhotos). Arrow buttons
    // rather than drag — reliable on iOS Safari inside a scrolling grid,
    // and 44px tap targets. Failed tiles are skipped by the move (they
    // have no row to renumber).
    const [reorderMode, setReorderMode] = useState(false);
    const [reorderDirty, setReorderDirty] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const movePhoto = (photo, dir) => {
        setPhotos(prev => {
            const i = prev.findIndex(p => p.id === photo.id);
            const j = i + dir;
            if (i < 0 || j < 0 || j >= prev.length) return prev;
            const next = [...prev];
            [next[i], next[j]] = [next[j], next[i]];
            return next;
        });
        setReorderDirty(true);
    };
    const finishReorder = async () => {
        if (!reorderDirty) { setReorderMode(false); return; }
        setSavingOrder(true);
        try {
            const ids = photos.filter(p => !p._failed).map(p => p.id);
            await db.reorderPhotos(ids);
            // Mirror the new numbering locally so a later sortPhotos()
            // (next upload) doesn't snap back to the old order.
            setPhotos(prev => {
                let n = 0;
                return prev.map(p => p._failed ? p : { ...p, sort_order: ++n });
            });
            setReorderDirty(false);
            setReorderMode(false);
            showToast('Photo order saved');
        } catch (e) {
            console.error('Reorder save failed:', e);
            showToast('Couldn\'t save order', true);
        } finally { setSavingOrder(false); }
    };
    // Helper: resolve the current set of tags on a photo, treating either
    // shape (room_tags array OR legacy room_tag string) uniformly. Returns
    // a fresh array — safe to mutate.
    const tagsOf = (photo) => {
        if (Array.isArray(photo.room_tags) && photo.room_tags.length) return [...photo.room_tags];
        if (photo.room_tag) return [photo.room_tag];
        return [];
    };

    const [activeTag, setActiveTag] = useState('');     // empty = not in tag mode
    // Custom free-text tag input in the gallery tag toolbar (Jaylen
    // 2026-07-29: "consignment", "To FL House" — same pattern as the
    // camera's + Custom). Arming sets activeTag like any chip; once a
    // custom tag lands on a photo it shows up as a regular chip via
    // tagCounts, so it stays re-tappable on later visits.
    const [customTagOpen, setCustomTagOpen] = useState(false);
    const [customTagText, setCustomTagText] = useState('');
    // Pending changes during tag mode: { [photoId]: string[] }
    // Only photos with edits show up here; on Save Tags we diff against the
    // server copy and write only what changed.
    const [pendingTags, setPendingTags] = useState({});
    const [savingTags, setSavingTags] = useState(false);
    const [lightboxPhoto, setLightboxPhoto] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState('');
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    // Counts for the toolbar chips. Counts reflect the SAVED state, not the
    // pending edits — this avoids a flickery feel as you toggle photos.
    const tagCounts = useMemo(() => {
        const counts = {};
        photos.forEach(p => tagsOf(p).forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
        return counts;
    }, [photos]);

    const inTagMode = !!activeTag;

    // The set of tags that WOULD be on a given photo right now, taking
    // pending edits into account. Used both for ring color logic and Save.
    const effectiveTags = (photo) => pendingTags[photo.id] ?? tagsOf(photo);

    // Accepts plain Files (file inputs) OR { file, tags } entries (the
    // in-app camera's tag-as-you-shoot) — tags can't ride on a File
    // object, so they travel alongside and land in room_tags at upload.
    const handleFiles = async (files) => {
        if (!files?.length) return;
        const entries = Array.from(files)
            .map(f => (f && f.file instanceof File) ? { file: f.file, tags: Array.isArray(f.tags) ? f.tags : [] } : { file: f, tags: [] })
            .filter(({ file: f }) => f instanceof File && (
                /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(f.name) || ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(f.type)
            ));
        const imageFiles = entries;
        if (!imageFiles.length) { showToast('No supported images found', true); return; }

        setUploading(true);
        const batchId = `wt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const total = imageFiles.length;
        let succeeded = 0;
        let failed = 0;
        // Initial state — gives the user immediate feedback that the batch
        // is running. Counter ticks up as each pool worker completes.
        setUploadProgress(`Uploading 0 of ${total}…`);

        // Per-file pipeline: HEIC convert (if needed) → client-side downscale
        // (2400px max edge, JPEG q=0.85) → upload + DB row. Multiple files
        // run in parallel via uploadInPool, but the steps inside each
        // worker are sequential because they're CPU/memory-bound on the
        // same source file. Helpers live in src/lib/imageUtils.js so the
        // office-side PhotoUpload component shares the same pipeline.
        // Picker order survives parallel uploads (sql/089, 2026-09-11):
        // each photo is stamped batchStart + its index in the selection.
        const batchStart = Date.now();
        const worker = async (entry, index) => {
            const file = entry.file;
            const tags = entry.tags || [];
            let ok = false;
            let processed = null;
            try {
                // The big win — without this, 60 12MP iPhone photos =
                // ~400 MB to Supabase. After this, the same batch is ~30 MB.
                processed = await processImageForUpload(file);

                const photo = await db.uploadPhoto(processed, clientId, {
                    // Tag-as-you-shoot tags ride straight in; file-input
                    // uploads arrive untagged (tagging happens after).
                    room_tags: tags,
                    source: 'estimator',
                    upload_batch_id: batchId,
                    batch_label: `Walkthrough ${new Date().toLocaleDateString()}`,
                    sort_order: batchStart + index,
                });
                // Append as each finishes, then re-sort by sort_order so the
                // grid settles into picker order even while the batch is
                // still uploading (failed tiles have none — they stay at
                // the end until retried).
                setPhotos(prev => sortPhotos([...prev, photo]));
                ok = true;
            } catch (e) {
                // Individual failure shouldn't kill the batch — keep the
                // (processed) blob in memory and render a RETRY tile in the
                // grid, so a signal drop never means re-shooting the photo
                // (Jaylen 2026-07-02). Blob lives until page reload.
                console.error('Upload failed for', file.name, e);
                const blob = processed || file;
                let previewUrl = null;
                try { previewUrl = URL.createObjectURL(blob); } catch { /* no preview */ }
                setPhotos(prev => [...prev, {
                    id: `failed_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    _failed: true,
                    _file: blob,
                    _processed: !!processed,
                    _batchId: batchId,
                    _previewUrl: previewUrl,
                    _tags: tags,
                    _sortOrder: batchStart + index,
                }]);
            } finally {
                if (ok) succeeded++; else failed++;
                const done = succeeded + failed;
                setUploadProgress(
                    failed
                        ? `Uploading ${done} of ${total}… (${failed} failed)`
                        : `Uploading ${done} of ${total}…`
                );
            }
        };

        await uploadInPool(imageFiles, worker, UPLOAD_CONCURRENCY);

        setUploading(false);
        setUploadProgress('');
        // Any successful upload counts as walkthrough activity — stamps
        // walkthrough_completed_at on the client (first time only).
        if (succeeded > 0 && onActivity) onActivity();
        if (failed === 0) {
            showToast(`${succeeded} photo${succeeded !== 1 ? 's' : ''} uploaded`);
        } else if (succeeded === 0) {
            showToast(`All ${total} uploads failed`, true);
        } else {
            showToast(`${succeeded} of ${total} uploaded — ${failed} failed`, true);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (cameraInputRef.current) cameraInputRef.current.value = '';
    };

    // Retry one failed upload — reuses the stored blob (already processed
    // when the failure was network-side, so retry skips the HEIC/downscale
    // work and just re-attempts the transfer).
    const retryUpload = async (failedPhoto) => {
        setPhotos(prev => prev.map(p => p.id === failedPhoto.id ? { ...p, _retrying: true } : p));
        try {
            const processed = failedPhoto._processed
                ? failedPhoto._file
                : await processImageForUpload(failedPhoto._file);
            const photo = await db.uploadPhoto(processed, clientId, {
                room_tags: failedPhoto._tags || [],
                source: 'estimator',
                upload_batch_id: failedPhoto._batchId,
                batch_label: `Walkthrough ${new Date().toLocaleDateString()}`,
                sort_order: failedPhoto._sortOrder ?? undefined,
            });
            if (failedPhoto._previewUrl) { try { URL.revokeObjectURL(failedPhoto._previewUrl); } catch { /* ignore */ } }
            setPhotos(prev => sortPhotos(prev.map(p => p.id === failedPhoto.id ? photo : p)));
            onActivity?.();
            showToast('Photo uploaded');
        } catch (e) {
            setPhotos(prev => prev.map(p => p.id === failedPhoto.id ? { ...p, _retrying: false } : p));
            showToast('Still no signal — photo kept, try again', true);
        }
    };

    const handleDelete = async (photo) => {
        // Failed-upload tiles only exist locally — discard without a server call.
        if (photo._failed) {
            if (photo._previewUrl) { try { URL.revokeObjectURL(photo._previewUrl); } catch { /* ignore */ } }
            setPhotos(prev => prev.filter(p => p.id !== photo.id));
            return;
        }
        try {
            await db.deletePhoto(photo);
            setPhotos(prev => prev.filter(p => p.id !== photo.id));
            // Drop any pending edit for this photo too.
            setPendingTags(prev => { const next = { ...prev }; delete next[photo.id]; return next; });
            showToast('Photo deleted');
        } catch (e) {
            console.error('Photo delete failed:', e);
            showToast('Delete failed', true);
        }
    };

    // Toggle the active tag on a photo (local pending state only).
    const togglePhotoTag = (photo) => {
        if (!activeTag) return;
        const current = effectiveTags(photo);
        const has = current.includes(activeTag);
        const next = has ? current.filter(t => t !== activeTag) : [...current, activeTag];
        setPendingTags(prev => ({ ...prev, [photo.id]: next }));
    };

    // Commit all pending tag edits to the DB. Only writes photos whose
    // pending array actually differs from the saved one (cheap defensive
    // check since the user may have toggled back to original).
    const handleSaveTags = async () => {
        const ids = Object.keys(pendingTags);
        if (ids.length === 0) { setActiveTag(''); return; }
        setSavingTags(true);
        let saved = 0; let failed = 0;
        const updatedById = {};
        for (const id of ids) {
            const photo = photos.find(p => p.id === id);
            if (!photo) continue;
            const before = tagsOf(photo).slice().sort();
            const after = (pendingTags[id] || []).slice().sort();
            if (JSON.stringify(before) === JSON.stringify(after)) continue;
            try {
                const updated = await db.updatePhotoTags(id, pendingTags[id] || []);
                updatedById[id] = updated;
                saved++;
            } catch (e) {
                console.error('Save tags failed for', id, e);
                failed++;
            }
        }
        if (saved > 0) {
            setPhotos(prev => prev.map(p => updatedById[p.id] ? { ...p, ...updatedById[p.id] } : p));
        }
        setPendingTags({});
        setActiveTag('');
        setSavingTags(false);
        if (failed > 0) showToast(`${saved} saved, ${failed} failed`, true);
        else if (saved > 0) showToast(`${saved} photo${saved !== 1 ? 's' : ''} tagged`);
        else showToast('No changes to save');
    };

    const handleCancelTagging = () => {
        setActiveTag('');
        setPendingTags({});
    };

    // Lightbox navigation — same chronological order as the grid, minus
    // failed-upload tiles (no server row / no url to show fullscreen).
    const lightboxList = photos.filter(p => !p._failed);
    const lightboxIndex = lightboxPhoto ? lightboxList.findIndex(p => p.id === lightboxPhoto.id) : -1;
    const goPrev = (e) => {
        if (e) e.stopPropagation();
        if (lightboxIndex <= 0) return;
        setLightboxPhoto(lightboxList[lightboxIndex - 1]);
    };
    const goNext = (e) => {
        if (e) e.stopPropagation();
        if (lightboxIndex < 0 || lightboxIndex >= lightboxList.length - 1) return;
        setLightboxPhoto(lightboxList[lightboxIndex + 1]);
    };

    useEffect(() => {
        if (!lightboxPhoto) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'Escape') setLightboxPhoto(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxPhoto, lightboxIndex, photos]);

    // Preload neighbor images so prev/next feels instant. We hit the same
    // transformed URL the lightbox itself uses, so the browser cache is
    // already warm by the time the user hits the arrow.
    useEffect(() => {
        if (!lightboxPhoto || lightboxIndex < 0) return;
        const neighbors = [lightboxList[lightboxIndex - 1], lightboxList[lightboxIndex + 1]].filter(Boolean);
        const opts = { width: 1600, height: 1600, resize: 'contain', quality: 80 };
        neighbors.forEach(p => {
            if (!p?.url) return;
            const img = new Image();
            img.src = getThumbnailUrl(p.url, opts);
        });
    }, [lightboxPhoto, lightboxIndex, photos]);

    // ─── Touch gestures: swipe-nav + pinch-zoom/pan ──────────────────────
    // Swipe (at 1x zoom): right → prev, left → next; vertical-leaning
    // gestures ignored; flick velocity > 0.11 px/ms OR drag > 60px — Emil's
    // gesture rules, same as the desktop PhotoGallery.
    //
    // Zoom (Jaylen 2026-07-29 mirroring feedback): the app's viewport meta
    // sets user-scalable=no (deliberate — app-feel), so BROWSER pinch-zoom
    // is dead everywhere and the lightbox needs its own. Two-finger pinch
    // scales 1x–4x anchored near the pinch point; one finger pans while
    // zoomed (clamped to the image); double-tap toggles 1x ↔ 2.5x at the
    // tap point. While zoomed, swipe-nav is OFF (a pan is not a page turn).
    // Transform is applied imperatively to the <img> (ref + style) so
    // touchmove doesn't re-render React 60×/s. The overlay carries
    // touch-action:none so Safari hands us raw touches instead of trying
    // (and failing) to scroll/zoom the page.
    const touchRef = useRef({ x: 0, y: 0, t: 0 });
    const zoomRef = useRef({ scale: 1, tx: 0, ty: 0 });
    const gestureRef = useRef(null);
    const lastTapRef = useRef(0);
    const zoomImgRef = useRef(null);
    const ZOOM_MAX = 4;
    const DOUBLE_TAP_SCALE = 2.5;

    const applyZoom = (animate) => {
        const el = zoomImgRef.current;
        if (!el) return;
        const { scale, tx, ty } = zoomRef.current;
        el.style.transition = animate ? 'transform 180ms ease-out' : 'none';
        el.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    };
    const clampPan = () => {
        const el = zoomImgRef.current;
        const z = zoomRef.current;
        if (!el || z.scale <= 1) { z.tx = 0; z.ty = 0; return; }
        // offsetWidth/Height = untransformed layout size, so the bound is
        // how far a scaled edge can travel before showing black past it.
        const maxX = (el.offsetWidth * (z.scale - 1)) / 2;
        const maxY = (el.offsetHeight * (z.scale - 1)) / 2;
        z.tx = Math.max(-maxX, Math.min(maxX, z.tx));
        z.ty = Math.max(-maxY, Math.min(maxY, z.ty));
    };
    const resetZoom = (animate) => {
        zoomRef.current = { scale: 1, tx: 0, ty: 0 };
        applyZoom(animate);
    };
    // New photo (tap, chevron, or swipe) always starts at 1x.
    useEffect(() => { resetZoom(false); }, [lightboxPhoto?.id]);

    const touchDist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const onLightboxTouchStart = (e) => {
        if (e.touches.length === 2) {
            const [a, b] = [e.touches[0], e.touches[1]];
            gestureRef.current = {
                type: 'pinch',
                startDist: touchDist(a, b),
                startScale: zoomRef.current.scale,
                startTx: zoomRef.current.tx,
                startTy: zoomRef.current.ty,
                midX: (a.clientX + b.clientX) / 2,
                midY: (a.clientY + b.clientY) / 2,
            };
            return;
        }
        const t = e.touches[0];
        touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
        gestureRef.current = { type: 'drag', lastX: t.clientX, lastY: t.clientY, moved: false };
    };
    const onLightboxTouchMove = (e) => {
        const g = gestureRef.current;
        if (!g) return;
        if (g.type === 'pinch' && e.touches.length === 2) {
            const [a, b] = [e.touches[0], e.touches[1]];
            const scale = Math.max(1, Math.min(ZOOM_MAX, g.startScale * (touchDist(a, b) / g.startDist)));
            const k = scale / g.startScale;
            const midX = (a.clientX + b.clientX) / 2;
            const midY = (a.clientY + b.clientY) / 2;
            const z = zoomRef.current;
            z.scale = scale;
            // Zoom about the pinch point (image sits centered in the
            // viewport), plus follow the midpoint's drift as a pan.
            z.tx = k * g.startTx + (1 - k) * (g.midX - window.innerWidth / 2) + (midX - g.midX);
            z.ty = k * g.startTy + (1 - k) * (g.midY - window.innerHeight / 2) + (midY - g.midY);
            clampPan();
            applyZoom(false);
        } else if (g.type === 'drag' && zoomRef.current.scale > 1 && e.touches.length === 1) {
            const t = e.touches[0];
            zoomRef.current.tx += t.clientX - g.lastX;
            zoomRef.current.ty += t.clientY - g.lastY;
            g.lastX = t.clientX; g.lastY = t.clientY; g.moved = true;
            clampPan();
            applyZoom(false);
        }
    };
    const onLightboxTouchEnd = (e) => {
        const g = gestureRef.current;
        if (e.touches.length > 0) {
            // One finger lifted mid-pinch — hand off to a pan without
            // re-triggering tap/swipe logic.
            const t = e.touches[0];
            gestureRef.current = { type: 'drag', lastX: t.clientX, lastY: t.clientY, moved: true };
            return;
        }
        gestureRef.current = null;
        if (g?.type === 'pinch') {
            // Snap back from a near-1x release so the image never sits
            // slightly-off; keep any real zoom as-is.
            if (zoomRef.current.scale < 1.15) resetZoom(true);
            return;
        }
        const t = e.changedTouches[0];
        const dx = t.clientX - touchRef.current.x;
        const dy = t.clientY - touchRef.current.y;
        const elapsed = Date.now() - touchRef.current.t;
        const isTap = !g?.moved && Math.hypot(dx, dy) < 12 && elapsed < 350;
        // Double-tap: zoom to 2.5x at the tap point, or back to 1x.
        if (isTap) {
            const now = Date.now();
            if (now - lastTapRef.current < 300) {
                lastTapRef.current = 0;
                if (zoomRef.current.scale > 1) { resetZoom(true); return; }
                const z = zoomRef.current;
                z.scale = DOUBLE_TAP_SCALE;
                z.tx = (window.innerWidth / 2 - t.clientX) * (DOUBLE_TAP_SCALE - 1);
                z.ty = (window.innerHeight / 2 - t.clientY) * (DOUBLE_TAP_SCALE - 1);
                clampPan();
                applyZoom(true);
                return;
            }
            lastTapRef.current = now;
        }
        if (zoomRef.current.scale > 1) return; // pan, not a page-turn
        if (Math.abs(dy) > Math.abs(dx)) return;
        const velocity = Math.abs(dx) / Math.max(elapsed, 1);
        const isFlick = velocity > SWIPE_VELOCITY;
        const isLongDrag = Math.abs(dx) > SWIPE_DISTANCE;
        if (!isFlick && !isLongDrag) return;
        if (dx > 0) goPrev();
        else goNext();
    };

    const pendingCount = Object.keys(pendingTags).length;

    // In-app rapid camera (Jaylen 2026-07-02): the native file-input camera
    // forces shoot → "Use Photo" → re-tap for EVERY shot — brutal at 40+
    // photos. The in-app viewfinder shoots as fast as you can tap; falls
    // back to the native camera when getUserMedia is unavailable/denied.
    const [cameraOpen, setCameraOpen] = useState(false);
    const openCamera = () => {
        if (navigator.mediaDevices?.getUserMedia) setCameraOpen(true);
        else cameraInputRef.current?.click();
    };

    return (
        <div className="space-y-3">
            {cameraOpen && createPortal(
                <InAppCamera
                    clientId={clientId}
                    onDone={(entries) => { setCameraOpen(false); if (entries.length) handleFiles(entries); }}
                    onFallback={() => { setCameraOpen(false); cameraInputRef.current?.click(); }}
                />,
                document.body,
            )}
            {/* Capture buttons — always available. Tagging happens after. */}
            <div className="grid grid-cols-2 gap-2">
                <Button size="lg" full onClick={openCamera} disabled={uploading}
                    leftIcon={<IconCameraGlyph size={20} />}>
                    Camera
                </Button>
                <Button variant="secondary" size="lg" full onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    leftIcon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}>
                    Gallery
                </Button>
            </div>

            {/* Reactive guidance — only useful AFTER someone hits a stall.
                Deliberately doesn't speculate about cause (we don't know),
                just gives an actionable next step. */}
            {!uploading && (
                <p className="text-[11px] text-slate-400 text-center px-2">
                    Having trouble uploading? Try splitting into smaller batches.
                </p>
            )}

            {uploading && (
                <div className="bg-white border border-slate-200 rounded-xl p-3 text-center" aria-live="polite">
                    <div className="text-[13px] text-slate-700 font-semibold">{uploadProgress}</div>
                </div>
            )}

            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFiles(e.target.files)} />
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />

            {/* Tag toolbar — only render once there are photos to tag. */}
            {photos.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-2.5 sticky top-0 z-30">
                    <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                            {inTagMode ? `Tap photos to apply: ${activeTag}` : reorderMode ? 'Use ◀ ▶ to move photos' : 'Tap a tag, then tap photos'}
                        </label>
                        {inTagMode && (
                            <span className="text-[11px] text-red-600 font-semibold">{pendingCount} pending</span>
                        )}
                        {!inTagMode && photos.length > 1 && (
                            reorderMode ? (
                                <Button size="sm" onClick={finishReorder} loading={savingOrder}>
                                    {reorderDirty ? 'Done · Save order' : 'Done'}
                                </Button>
                            ) : (
                                <Button variant="secondary" size="sm" onClick={() => setReorderMode(true)}>
                                    Reorder
                                </Button>
                            )
                        )}
                    </div>
                    {reorderMode ? null : customTagOpen ? (
                        <div className="flex items-center gap-2 pb-1">
                            <input autoFocus value={customTagText} onChange={(e) => setCustomTagText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && customTagText.trim()) { setActiveTag(customTagText.trim()); setCustomTagOpen(false); } }}
                                placeholder='Custom tag — "Consignment", "To FL House"…'
                                className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400" />
                            <Button size="sm" disabled={!customTagText.trim()}
                                onClick={() => { setActiveTag(customTagText.trim()); setCustomTagOpen(false); }}>Use</Button>
                            <IconButton label="Close" size="sm" onClick={() => setCustomTagOpen(false)}><IconX size={16} /></IconButton>
                        </div>
                    ) : (
                    <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-2.5 px-2.5" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {(() => {
                            // Standard rooms + every custom tag already on this
                            // client's photos + a just-armed custom tag that
                            // hasn't been saved onto anything yet.
                            const customInUse = Object.keys(tagCounts).filter(t => !ROOM_TAGS.includes(t)).sort();
                            const chips = [...ROOM_TAGS, ...customInUse];
                            if (activeTag && !chips.includes(activeTag)) chips.push(activeTag);
                            return chips.map(tag => {
                                const count = tagCounts[tag] || 0;
                                const active = activeTag === tag;
                                return (
                                    <button key={tag} onClick={() => setActiveTag(active ? '' : tag)}
                                        className={`h-8 px-3 rounded-full text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-colors ${active ? 'bg-red-600 text-white' : count > 0 ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-white border border-slate-200 text-slate-600'}`}>
                                        {tag}{count > 0 ? ` (${count})` : ''}
                                    </button>
                                );
                            });
                        })()}
                        <button onClick={() => { setCustomTagText(''); setCustomTagOpen(true); }}
                            className="h-8 px-3 rounded-full text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-colors bg-white border border-dashed border-slate-300 text-slate-500">
                            + Custom
                        </button>
                    </div>
                    )}
                    {inTagMode && (
                        <div className="flex gap-2 mt-2.5">
                            <Button variant="secondary" size="sm" className="flex-1" onClick={handleCancelTagging} disabled={savingTags}>
                                Cancel
                            </Button>
                            <Button size="sm" className="flex-1" onClick={handleSaveTags} loading={savingTags}>
                                {`Save tags${pendingCount ? ` (${pendingCount})` : ''}`}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Photo grid — flat chronological order. */}
            {loading ? (
                /* Skeleton while the initial photo fetch is in flight —
                   without this the grid showed "No photos yet" during the
                   fetch, which on a slow connection read as data loss and
                   risked the estimator re-shooting existing photos. */
                <div className="grid grid-cols-3 gap-1.5" aria-label="Loading photos">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="aspect-square rounded-lg bg-slate-200 animate-pulse" />
                    ))}
                </div>
            ) : photos.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2 text-slate-300"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    <p className="text-[14px]">No photos yet</p>
                    <p className="text-[12px] mt-1">Tap Camera or Gallery to start</p>
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-1.5">
                    {photos.map(photo => {
                        // Failed-upload tile — dimmed preview + RETRY overlay.
                        // Tap anywhere retries; X discards. Never enters tag
                        // mode or the lightbox (it has no server row yet).
                        if (photo._failed) {
                            return (
                                <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-slate-300"
                                    onClick={() => { if (!photo._retrying) retryUpload(photo); }}>
                                    {photo._previewUrl && (
                                        <img src={photo._previewUrl} alt="" className="w-full h-full object-cover opacity-40" />
                                    )}
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${photo._retrying ? 'bg-slate-700 text-white' : 'bg-rose-600 text-white'}`}>
                                            {photo._retrying ? 'Retrying…' : '↻ Tap to retry'}
                                        </span>
                                        <span className="text-[9px] font-semibold text-slate-700 bg-white/70 px-1.5 py-0.5 rounded">upload failed</span>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(photo); }}
                                        className="absolute top-0 right-0 p-2 flex items-center justify-center"
                                        aria-label="Discard failed photo"
                                    >
                                        <span className="w-7 h-7 bg-black/60 text-white rounded-full text-[12px] flex items-center justify-center">{'✕'}</span>
                                    </button>
                                </div>
                            );
                        }
                        const tags = effectiveTags(photo);
                        const hasActive = inTagMode && tags.includes(activeTag);
                        const ringCls = inTagMode
                            ? (hasActive ? 'ring-4 ring-red-500' : 'ring-2 ring-slate-200')
                            : '';
                        const idx = photos.indexOf(photo);
                        return (
                            <div key={photo.id} className={`relative aspect-square rounded-lg overflow-hidden bg-slate-200 cursor-pointer ${ringCls}${reorderMode ? ' ring-2 ring-indigo-300' : ''}`}
                                style={{ contentVisibility: 'auto', containIntrinsicSize: '150px 150px' }}
                                onClick={() => reorderMode ? null : inTagMode ? togglePhotoTag(photo) : setLightboxPhoto(photo)}>
                                <img {...thumbProps(photo.url, { width: 300, height: 300 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                {reorderMode && (
                                    <>
                                        <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{idx + 1}</span>
                                        <div className="absolute inset-x-0 bottom-0 flex">
                                            <button type="button" onClick={(e) => { e.stopPropagation(); movePhoto(photo, -1); }} disabled={idx === 0}
                                                className="flex-1 h-11 bg-black/55 text-white text-lg font-bold flex items-center justify-center active:bg-black/75 disabled:opacity-30" aria-label="Move earlier">◀</button>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); movePhoto(photo, 1); }} disabled={idx === photos.length - 1}
                                                className="flex-1 h-11 bg-black/55 text-white text-lg font-bold flex items-center justify-center border-l border-white/20 active:bg-black/75 disabled:opacity-30" aria-label="Move later">▶</button>
                                        </div>
                                    </>
                                )}
                                {/* Tag pills overlay (when not in tag mode, lightweight peek of tags) */}
                                {!inTagMode && !reorderMode && tags.length > 0 && (
                                    <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5 pointer-events-none">
                                        {tags.slice(0, 2).map(t => (
                                            <span key={t} className="bg-black/60 text-white text-[9px] font-medium px-1.5 py-0.5 rounded">{t}</span>
                                        ))}
                                        {tags.length > 2 && <span className="bg-black/60 text-white text-[9px] font-medium px-1.5 py-0.5 rounded">+{tags.length - 2}</span>}
                                    </div>
                                )}
                                {/* Delete button — only show when NOT in tag mode to keep tag-toggling clean */}
                                {/* Visual circle stays 28px (a 44px circle would
                                    eat a quarter of the thumbnail) but the padded
                                    wrapper brings the tap target to ~44px. */}
                                {!inTagMode && !reorderMode && (
                                    <button
                                        onClick={async (e) => { e.stopPropagation(); if (await confirmDialog({ title: 'Delete this photo?', body: 'It will be removed from this walkthrough.', confirmLabel: 'Delete Photo' })) handleDelete(photo); }}
                                        className="absolute top-0 right-0 p-2 flex items-center justify-center"
                                        aria-label="Delete photo"
                                    >
                                        <span className="w-7 h-7 bg-black/60 text-white rounded-full text-[12px] flex items-center justify-center">{'\u2715'}</span>
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Lightbox — fullscreen single-photo view with prev/next +
                close. Portal to <body> so it escapes any transformed
                ancestor. Parity with the desktop PhotoGallery: huge hit
                zones, swipe gestures, always-rendered chevrons, backdrop-
                only close. */}
            {lightboxPhoto && createPortal((
                <div
                    className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
                    style={{ touchAction: 'none', overscrollBehavior: 'none' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setLightboxPhoto(null); }}
                    onTouchStart={onLightboxTouchStart}
                    onTouchMove={onLightboxTouchMove}
                    onTouchEnd={onLightboxTouchEnd}
                >
                    {/* Chevron buttons \u2014 ALWAYS rendered (disabled at
                        boundaries) so the hit-target stays at the same
                        screen position when you reach the first/last
                        photo. The BUTTON is a tall full-height side
                        strip; the visible pill is anchored inside.
                        Tap anywhere in the strip = navigate. */}
                    <button
                        onClick={(e) => { e.stopPropagation(); goPrev(e); }}
                        disabled={lightboxIndex <= 0}
                        className="group absolute left-0 top-0 bottom-0 w-32 z-10 flex items-center justify-start pl-3 disabled:cursor-not-allowed"
                        aria-label="Previous photo"
                    >
                        <span className="bg-white/90 group-disabled:opacity-30 text-slate-800 min-w-[60px] min-h-[60px] flex items-center justify-center rounded-full shadow-lg text-3xl font-bold leading-none transition-opacity">{'\u2039'}</span>
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); goNext(e); }}
                        disabled={lightboxIndex < 0 || lightboxIndex >= photos.length - 1}
                        className="group absolute right-0 top-0 bottom-0 w-32 z-10 flex items-center justify-end pr-3 disabled:cursor-not-allowed"
                        aria-label="Next photo"
                    >
                        <span className="bg-white/90 group-disabled:opacity-30 text-slate-800 min-w-[60px] min-h-[60px] flex items-center justify-center rounded-full shadow-lg text-3xl font-bold leading-none transition-opacity">{'\u203A'}</span>
                    </button>
                    {/* Landscape = the TV-mirroring orientation (Jaylen
                        2026-07-29): go truly edge-to-edge — 100dvh/100vw,
                        no rounded corners — so a 65" mirror has no black
                        band above/below. dvh (not vh) so plain-Safari's
                        collapsing chrome doesn't crop the image; portrait
                        keeps the 95vw/90vh framing. */}
                    <div className="relative max-w-[95vw] max-h-[94vh] landscape:max-w-[100vw] landscape:max-h-[100dvh]" onClick={e => e.stopPropagation()}>
                        {/* No `key` on <img> — neighbor preloading (effect
                            above) already warms the browser cache so the
                            swap is instant. The previous `key={photo.id}`
                            forced a full element unmount/remount which
                            briefly blanked the frame; preload makes that
                            defensive code unnecessary. The src goes through
                            Supabase image transforms (~200-400KB instead of
                            multi-MB originals); thumbProps falls back to
                            the raw URL if the transform endpoint is off.
                            90vh (was 80vh) — screen-mirroring note
                            (Jaylen 2026-07-08): fill as much of the frame
                            as possible; the tag strip + close X overlay
                            the image, nothing sits below it. */}
                        <img
                            ref={zoomImgRef}
                            {...thumbProps(lightboxPhoto.url, { width: 1600, height: 1600, resize: 'contain', quality: 80 })}
                            alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg bg-black landscape:max-h-[100dvh] landscape:rounded-none"
                            style={{ transformOrigin: 'center center' }} />
                        {/* Close X anchored to the IMAGE's top-right corner,
                            not the viewport. The image is always centered
                            well below the iOS status bar / Dynamic Island /
                            active call banner, so this lands in a tappable
                            spot regardless of system chrome state. Same
                            pattern as the desktop PhotoGallery. Previous
                            attempt with env(safe-area-inset-top) didn't
                            work because in regular Safari (not PWA) the
                            inset returns 0 — only standalone mode gets a
                            non-zero value. */}
                        <button onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }}
                            className="absolute top-2 right-2 z-20 bg-white/90 hover:bg-white text-slate-800 w-10 h-10 rounded-full shadow-lg flex items-center justify-center text-xl font-bold"
                            aria-label="Close">{'✕'}</button>
                        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1 items-center">
                            {tagsOf(lightboxPhoto).map(t => (
                                <span key={t} className="bg-black/70 text-white text-[11px] font-medium px-2 py-0.5 rounded">{t}</span>
                            ))}
                            {photos.length > 1 && (
                                <span className="ml-auto bg-black/70 text-white text-[11px] px-2 py-0.5 rounded">{lightboxIndex + 1} / {photos.length}</span>
                            )}
                        </div>
                    </div>
                </div>
            ), document.body)}
            {confirmElement}
        </div>
    );
}

// ===== DETAILS TAB =====
// Save is LIFTED to WalkthroughCapture (2026-07-02) and driven by
// useAutosave there — this tab renders the fields, calls `onEdit()`
// (= autosave.touch) after every change, and shows the status line.
// Offline saves park in estimatorQueue and sync automatically.
// Dates are the exception (2026-09-18): the TripStrip writes the CURRENT
// MOVE on tap through `onDates` (saveMove, no debounce); `move` / `moves`
// / `onSwitchMove` drive the move switcher; `onConfirmAll` logs a
// confirmation of every tentative date under the estimator's name.
function DetailsTab({ details, setDetails, onEdit, autosave, move, moves = [], pendingDates = {}, onSwitchMove, onDates, onConfirmAll, confirming = false }) {
    const { config } = useStore();
    // What the strip shows: the move, any offline-parked taps on top, and
    // the form's live packing choice so the Pack tile appears the moment
    // "Partial" / "Full" is picked (not 1.2s later when the autosave
    // lands it on the move).
    const stripValue = move ? { ...move, ...pendingDates, packing_needed: details.packing_needed } : null;
    // Furniture tiers from app_config (StatusManager-customizable), with
    // built-in defaults. Short field-friendly blurbs — the desktop's full
    // subtitles are too long for a phone card.
    const furnitureOptions = getFurnitureClassOptions(config);
    const FURNITURE_HINTS = {
        good: 'Budget · quick-assembly',
        better: 'Mid-range · value',
        best: 'Solid wood · high-end',
        luxury: 'Bespoke · premium',
    };
    const set = (k, v) => { setDetails(prev => ({ ...prev, [k]: v })); onEdit?.(); };
    // Multi-key setter for RoutePointsEditor — collapses three writes
    // (origin_address / destination_address / additional_stops) into a
    // single render so the editor's auto-sync effect doesn't thrash. The
    // editor also syncs once on mount with the SAME values — only a real
    // change touches the autosave (otherwise opening the tab would "start"
    // the walkthrough).
    const setBatch = (patch) => {
        const changed = Object.keys(patch).some(k => (details[k] ?? '') !== (patch[k] ?? ''));
        setDetails(prev => ({ ...prev, ...patch }));
        if (changed) onEdit?.();
    };
    const textareaCls = `${mobileInputCls} min-h-[80px] py-3 leading-relaxed`;
    const packingItems = PACKING_OPTIONS.map(opt => ({
        value: opt.value,
        label: opt.value === 'none' ? 'None' : opt.value === 'partial' ? 'Partial' : 'Full',
    }));
    const furnitureItems = furnitureOptions.map(opt => ({ value: opt.key, label: opt.label, color: opt.color, bg: opt.bg }));

    return (
        <div className="space-y-3">
            {/* Move switcher (3B) — only when the client has more than one
                move; chips are 32px, no "+ New move" on the phone. */}
            {moves.length > 1 && (
                <MoveSwitcher moves={moves} currentId={move?.id} onSelect={onSwitchMove} className="px-0.5" />
            )}

            {/* Route — multi-stop editor with origin, optional middle
                stops, and destination, plus per-leg drive times and a
                trip total. Persists to origin_address /
                destination_address / additional_stops, matching the
                desktop MoveDetailsEditor's wire format byte-for-byte —
                and, since 2026-09-18, onto the CURRENT MOVE. */}
            <MobileCard title="Route">
                <p className="text-[11px] text-slate-400 mb-2">Tap "Add Stop" if the move has multiple pickups or drop-offs. Use the arrows to reorder.</p>
                <RoutePointsEditor form={details} set={set} setBatch={setBatch} />
            </MobileCard>

            {/* Move dates — the trip strip (1B), THE move-date control
                (moves-architecture.md §4). Pack · Load · Unload tiles, each
                tentative on its own; a tap saves to the move straight away.
                Its own label row carries "+ Pack day" / "+ Unload date",
                so it stands in for the card title. */}
            <MobileCard>
                {stripValue ? (
                    <>
                        <TripStrip value={stripValue} onChange={onDates} size="md" className="!border-0 !p-0 !rounded-none" />
                        {/* On-site confirmation (rule 5: a log, not a lock).
                            Shown only while something is still tentative. */}
                        {anyTentative(stripValue) && (
                            <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-100">
                                <span className="text-[12px] text-slate-600">Dates confirmed with the client?</span>
                                <Button variant="tertiary" size="sm" loading={confirming} onClick={onConfirmAll}>Confirm all</Button>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-[12px] text-slate-500">No move on file yet — the office adds one from the client file, then the dates show here.</p>
                )}
            </MobileCard>

            {/* Furniture Quality (sql/048) — the estimator's in-home judgment;
                informs crew/care allocation. Same tiers as the office.
                Tapping the lit key clears it (optional field). */}
            <MobileCard title="Furniture quality">
                <ChoiceKeys
                    columns={2}
                    ariaLabel="Furniture quality"
                    items={furnitureItems}
                    value={details.furniture_class || ''}
                    onChange={v => set('furniture_class', v === details.furniture_class ? '' : v)}
                    keyClassName="!h-11"
                />
                {details.furniture_class && FURNITURE_HINTS[details.furniture_class] && (
                    <p className="text-[11px] text-slate-400 mt-2">{FURNITURE_HINTS[details.furniture_class]}</p>
                )}
            </MobileCard>

            {/* Property Details */}
            <MobileCard title="Property details" className="space-y-2.5">
                {/* Per-location: Sq Ft + Stairs + Elevator for BOTH pickup and
                    destination (destination sq ft was missing before — only
                    from_sqft was captured). Stairs/Elevator are Switches
                    (form-controls.md) — the native checkbox indicator collapses
                    to 0 width on iOS Safari. */}
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                        <span className="text-[12px] font-semibold text-slate-600">Pickup</span>
                        <label className="text-[11px] text-slate-400 block">Approx. Sq Ft</label>
                        <NumberField value={details.from_sqft} onChange={v => set('from_sqft', v === '' ? '' : String(Math.max(0, parseInt(v) || 0)))} step={100} min={0} suffix="sq ft" placeholder="e.g. 1500" ariaLabel="Pickup approx. sq ft" />
                        <Switch
                            label="Stairs"
                            checked={!!details.from_stairs}
                            onChange={on => set('from_stairs', on)}
                        />
                        <Switch
                            label="Elevator"
                            checked={!!details.from_elevator}
                            onChange={on => set('from_elevator', on)}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <span className="text-[12px] font-semibold text-slate-600">Destination</span>
                        <label className="text-[11px] text-slate-400 block">Approx. Sq Ft</label>
                        <NumberField value={details.to_sqft} onChange={v => set('to_sqft', v === '' ? '' : String(Math.max(0, parseInt(v) || 0)))} step={100} min={0} suffix="sq ft" placeholder="e.g. 1500" ariaLabel="Destination approx. sq ft" />
                        <Switch
                            label="Stairs"
                            checked={!!details.to_stairs}
                            onChange={on => set('to_stairs', on)}
                        />
                        <Switch
                            label="Elevator"
                            checked={!!details.to_elevator}
                            onChange={on => set('to_elevator', on)}
                        />
                    </div>
                </div>

                {/* Packing — None / Partial / Full (matches the office; was a
                    broken boolean that lost the partial/full distinction). When
                    partial or full, a note captures WHAT needs packing. */}
                <div className="space-y-2 pt-1">
                    <span className="text-[12px] font-semibold text-slate-600 block">Packing</span>
                    <ChoiceKeys
                        columns={3}
                        ariaLabel="Packing"
                        items={packingItems}
                        value={details.packing_needed || 'tbd'}
                        onChange={v => set('packing_needed', v)}
                        keyClassName="!h-11"
                    />
                    {(details.packing_needed === 'partial' || details.packing_needed === 'full') && (
                        <textarea className={textareaCls} value={details.packing_notes}
                            onChange={e => set('packing_notes', e.target.value)}
                            placeholder="What needs packing? e.g. just the artwork, 8 lamps, kitchen only…" />
                    )}
                </div>
            </MobileCard>

            {/* Access Notes */}
            <MobileCard title="Access & parking">
                <textarea className={textareaCls} value={details.access_notes} onChange={e => set('access_notes', e.target.value)}
                    placeholder="Parking, gate codes, loading dock, narrow streets, long carry..." />
            </MobileCard>

            {/* Heavy Items */}
            <MobileCard title="Heavy / specialty items">
                <textarea className={textareaCls} value={details.heavy_items} onChange={e => set('heavy_items', e.target.value)}
                    placeholder="Piano, gun safe, pool table, appliances..." />
            </MobileCard>

            {/* Autosave status line (feedback.md 5C) — no Save button. */}
            <div className="text-center py-1">
                <AutosaveStatus status={autosave.status} savedAt={autosave.savedAt} error={autosave.error} />
            </div>
        </div>
    );
}

// ===== NOTES TAB =====
// The SAME unified Office + Crew notes panel the desktop client file uses
// (shared <ClientNotesPanel>, same internal_note_sets / crew_note_sets rows),
// so notes sync both directions between the estimator app and the office —
// no more parallel walkthrough_notes/special_notes columns to reconcile
// (those were migrated into Office Notes, sql/066). onActivity marks the
// walkthrough in-progress when a note is saved.
function NotesTab({ client, onActivity }) {
    const { user } = useAuth();
    const estimatorName = (() => {
        const raw = (user?.email || '').split('@')[0] || 'Estimator';
        return raw.charAt(0).toUpperCase() + raw.slice(1);
    })();
    return (
        <div>
            <ClientNotesPanel clientId={client.id} onActivity={onActivity} defaultOfficeLabel={`${estimatorName}'s Walkthrough note`} />
        </div>
    );
}

export default WalkthroughPortal;
